import React from "react";
import { Chip, Divider, Stack, Typography } from "@mui/material";
import { blueGrey, grey } from "@mui/material/colors";
import { InlineWidget } from "react-calendly";
import { Link } from "react-router-dom";
import { Link as MuiLink } from "@mui/material";

import { LottieIcon, PageContainer } from "src/shared/ui";
import { LottieIconNames } from "src/shared/config";
import { ContactCard } from "src/widgets/ContactCard";
import executiveImg from "src/shared/assets/officers/executive_officer.png";

export const ContactPage: React.FC = () => {
  return (
    <PageContainer
      hidden
      mainComponent={
        <>
          <Stack
            width={1}
            height={1}
            direction="column"
            justifyContent="center"
            alignItems="flex-start"
            spacing={2}
            zIndex={99}
            sx={{
              alignItems: {
                xs: "center",
                sm: "flex-start",
              },
            }}
          >
            <Typography
              variant="h1"
              fontWeight="bold"
              color={blueGrey[700]}
              textTransform="uppercase"
              textAlign="center"
            >
              Contact us
            </Typography>
            <Typography
              variant="h5"
              color={grey[700]}
              textAlign="center"
              textTransform="uppercase"
            >
              We are MathrixAI
            </Typography>
          </Stack>

          <Stack
            sx={{
              display: {
                xs: "none",
                sm: "flex",
              },
            }}
          >
            <LottieIcon
              icon={LottieIconNames.CONTACT}
              autoPlay
              loop
              style={{
                position: "absolute",
                height: "calc(100% - 180px)",
                transform: "translateX(150px)",
                top: 0,
                right: 0,
              }}
            />
          </Stack>
        </>
      }
    >
      <Typography
        maxWidth="md"
        variant="h3"
        textAlign="center"
        fontWeight={500}
        color="primary.dark"
        marginBottom={4}
      >
        With headquarters in Berlin, Germany, and a research hub in Yerevan,
        Armenia, MathrixAI collaborates with partners and clients worldwide,
        adapting our expertise to meet the needs of different industries and
        markets.
      </Typography>

      <Stack
        width={1}
        maxWidth="md"
        direction="row"
        gap={4}
        alignItems="stretch"
        sx={{
          alignItems: {
            xs: "center",
            sm: "stretch",
          },
          flexDirection: {
            xs: "column",
            sm: "row",
          },
        }}
      >
        <ContactCard
          image={executiveImg}
          fullName="Sven Badalyan"
          position="Chief Executive Officer"
          phoneNumber="+49 162 7344423"
          email="sven@badalyan.it"
          linkedinURL="linkedin.com/in/sven-badalyan"
        />

        <Stack width={1}>
          <InlineWidget
            url="https://calendly.com/vahan-simonyan-ca"
            styles={{ width: "100%", height: "100%" }}
          />
        </Stack>
      </Stack>

      <Stack width={1} maxWidth="md" direction="column" gap={2}>
        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Our Offices
        </Typography>

        <Stack
          direction="row"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Stack flex={1} direction="row" alignItems="center" gap={2}>
            <Chip
              label="Berlin Office"
              color="warning"
              sx={{
                width: 190,
                minWidth: 190,
              }}
            />
            <Typography
              variant="subtitle2"
              fontWeight={300}
              color="primary.dark"
            >
              Landsberger Allee 273, 13055 Berlin, Germany
            </Typography>
          </Stack>
          <Stack flex={1} direction="row" alignItems="center" gap={2}>
            <Chip
              label="Yerevan Office"
              color="warning"
              sx={{
                width: 190,
                minWidth: 190,
              }}
            />
            <Typography
              variant="subtitle2"
              fontWeight={300}
              color="primary.dark"
            >
              Hakob Hakobyan 3, 033 Yerevan, Armenia
            </Typography>
          </Stack>
        </Stack>

        <Typography variant="body1" fontWeight={300}>
          For all inquiries—including business partnerships, technical support,
          or general questions—please contact us via the email provided above.
          We are committed to responding promptly and ensuring your questions
          are addressed effectively.
        </Typography>

        <Divider
          component="div"
          color="info.dark"
          sx={{
            width: 1,
          }}
        />

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Stay Connected
        </Typography>

        <Typography variant="body1" fontWeight={300}>
          Stay updated with our latest developments, news, and industry insights
          by following us on LinkedIn:{" "}
          <MuiLink
            sx={{
              cursor: "pointer",
            }}
          >
            <Link to="https://www.linkedin.com/company/mathrixai">
              https://www.linkedin.com/company/mathrixai
            </Link>
          </MuiLink>
        </Typography>

        <Divider
          component="div"
          color="info.dark"
          sx={{
            width: 1,
          }}
        />

        <Typography
          variant="h4"
          color={blueGrey[600]}
          letterSpacing={4}
          textTransform="uppercase"
        >
          Reach Out to Us
        </Typography>

        <Typography variant="body1" fontWeight={300}>
          Alternatively, you can contact us by completing the form below. We
          value your feedback and look forward to engaging with you. At
          MathrixAI, we prioritize open communication and collaboration. Let us
          know how we can assist you, and we will be pleased to connect.
        </Typography>
      </Stack>
    </PageContainer>
  );
};
